import DetailsCard from "@/components/DetailsCard";
import DetailsCardColumn from "@/components/DetailsCard/DetailsCardColumn";
import DetailsCardTwoColumnRow from "@/components/DetailsCardTwoColumnRow";
import SectionHeading from "@/components/SectionHeading";
import { ExternalLink } from "@/components/withLink";
import { formattedAddress } from "@/lib/addressHelpers";
import { renderEnumDescription } from "@/lib/enumHelpers";
import { ensureProtocol } from "@/lib/urlUtils";
import { useNavigate } from "@/router";
import type { EnumOptions, FacilityOperatingStatus, FacilityType } from "@/types";
import { VIEW_SECTION_OFFICE_ACTION_LABEL } from "../../_utils";
import type { SectionOfficeFacilityDetails } from "../queries";

type Props = {
  facility: SectionOfficeFacilityDetails;
  operatingStatusOptions: EnumOptions<FacilityOperatingStatus>;
  facilityTypeOptions: EnumOptions<FacilityType>;
};

export function SectionOfficeContactDetails({ facility, operatingStatusOptions, facilityTypeOptions }: Props) {
  const navigate = useNavigate();

  return (
    <div data-testid="section-office-contact-details">
      <SectionHeading
        heading="Section Facility Contact"
        body="Basic contact details for this section's facility."
        actions={[
          {
            label: VIEW_SECTION_OFFICE_ACTION_LABEL,
            variant: "outlined",
            testId: "view-section-facility-details",
            onClick: () => {
              navigate("/facilities/:facilityId", { params: { facilityId: facility.id } });
            }
          }
        ]}
      />
      <DetailsCardTwoColumnRow
        narrowColumn={
          <DetailsCardColumn>
            <DetailsCard
              heading="Name"
              options={[
                {
                  iconName: "store",
                  headline: facility.name,
                  overline: "Facility Name",
                  copyable: !!facility.name,
                  copyText: facility.name || undefined
                }
              ]}
            />
            <DetailsCard
              heading="Official"
              options={[
                {
                  iconName: "location_on",
                  headline: renderEnumDescription(facility.facilityType, facilityTypeOptions),
                  overline: "Facility Type"
                },
                {
                  iconName: "door_open",
                  headline: renderEnumDescription(facility.operatingStatus, operatingStatusOptions),
                  overline: "Status"
                },
                {
                  iconName: "link",
                  headline:
                    facility.website != null ? (
                      <ExternalLink href={ensureProtocol(facility.website)}>{facility.website}</ExternalLink>
                    ) : null,
                  overline: "Website"
                }
              ]}
            />
          </DetailsCardColumn>
        }
        wideColumn={
          <DetailsCardColumn>
            <DetailsCard
              heading="Phone"
              options={[
                {
                  iconName: "call",
                  headline: facility.phone?.phoneFormatted,
                  overline: "Business",
                  copyable: !!facility.phone?.phoneFormatted,
                  copyText: facility.phone?.phoneFormatted ?? undefined
                }
              ]}
            />
            <DetailsCard
              heading="Address"
              options={[
                {
                  iconName: "location_on",
                  headline: facility.mainAddress != null ? formattedAddress(facility.mainAddress) : "",
                  overline: "Main",
                  copyable: facility.mainAddress != null,
                  copyText: facility.mainAddress != null ? formattedAddress(facility.mainAddress) : undefined
                }
              ]}
            />
          </DetailsCardColumn>
        }
      />
    </div>
  );
}
